import { useEffect, useRef, useCallback, useImperativeHandle, forwardRef } from 'react'
import { logger } from '../../lib/logger'

// Turnstile API type declarations
declare global {
  interface Window {
    turnstile?: {
      render: (
        container: HTMLElement | string,
        options: {
          sitekey: string
          callback?: (token: string) => void
          'error-callback'?: (error?: string) => void
          'expired-callback'?: () => void
          theme?: 'light' | 'dark' | 'auto'
          size?: 'normal' | 'flexible' | 'compact'
        }
      ) => string
      reset: (widgetId: string) => void
      remove: (widgetId: string) => void
      getResponse: (widgetId: string) => string | undefined
      isExpired: (widgetId: string) => boolean
    }
    onloadTurnstileCallback?: () => void
  }
}

interface TurnstileProps {
  siteKey: string
  onSuccess: (token: string) => void
  onError?: () => void
  onExpired?: () => void
}

export interface TurnstileRef {
  reset: () => void
}

const TURNSTILE_SCRIPT_ID = 'cf-turnstile-script'

export const Turnstile = forwardRef<TurnstileRef, TurnstileProps>(
  ({ siteKey, onSuccess, onError, onExpired }, ref) => {
    const containerRef = useRef<HTMLDivElement>(null)
    const widgetIdRef = useRef<string | null>(null)
    const renderAttemptedRef = useRef(false)

    const handleReset = useCallback(() => {
      if (widgetIdRef.current && window.turnstile) {
        window.turnstile.reset(widgetIdRef.current)
      }
    }, [])

    useImperativeHandle(ref, () => ({
      reset: handleReset,
    }))

    const renderWidget = useCallback(() => {
      logger.log('[Turnstile] renderWidget called', {
        hasContainer: !!containerRef.current,
        hasTurnstile: !!window.turnstile,
        hasWidgetId: !!widgetIdRef.current,
        renderAttempted: renderAttemptedRef.current,
        siteKey,
      })

      if (containerRef.current && window.turnstile && !widgetIdRef.current && !renderAttemptedRef.current) {
        renderAttemptedRef.current = true
        logger.log('[Turnstile] Attempting to render widget...')
        try {
          widgetIdRef.current = window.turnstile.render(containerRef.current, {
            sitekey: siteKey,
            callback: onSuccess,
            'error-callback': onError,
            'expired-callback': onExpired,
            theme: 'auto',
            size: 'flexible',
          })
          logger.log('[Turnstile] Widget rendered successfully, widgetId:', widgetIdRef.current)
        } catch (err) {
          logger.error('[Turnstile] Failed to render widget:', err)
          onError?.()
        }
      }
    }, [siteKey, onSuccess, onError, onExpired])

    useEffect(() => {
      logger.log('[Turnstile] useEffect running, checking for script...')

      // Check if script already exists
      let script = document.getElementById(TURNSTILE_SCRIPT_ID) as HTMLScriptElement | null
      logger.log('[Turnstile] Script element:', script ? 'exists' : 'does not exist')

      if (!script) {
        logger.log('[Turnstile] Creating new script element...')
        // Create script element - no async/defer for explicit rendering with callback
        script = document.createElement('script')
        script.id = TURNSTILE_SCRIPT_ID
        script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit&onload=onloadTurnstileCallback'

        // Set up the global callback before adding the script
        window.onloadTurnstileCallback = () => {
          logger.log('[Turnstile] onloadTurnstileCallback fired!')
          renderWidget()
        }

        script.onerror = () => {
          logger.error('[Turnstile] Failed to load script')
          onError?.()
        }

        document.head.appendChild(script)
        logger.log('[Turnstile] Script appended to head')
      } else if (window.turnstile) {
        logger.log('[Turnstile] Script exists and turnstile is available')
        // Script already loaded and turnstile available
        renderWidget()
      } else {
        logger.log('[Turnstile] Script exists but turnstile not yet available, setting callback...')
        // Script exists but turnstile not yet available - set callback
        window.onloadTurnstileCallback = () => {
          logger.log('[Turnstile] onloadTurnstileCallback fired (deferred)!')
          renderWidget()
        }
      }

      return () => {
        // Don't remove the script on unmount, other components may need it
      }
    }, [renderWidget, onError])

    // Cleanup widget on unmount
    useEffect(() => {
      return () => {
        if (widgetIdRef.current && window.turnstile) {
          try {
            window.turnstile.remove(widgetIdRef.current)
          } catch {
            // Ignore errors during cleanup
          }
          widgetIdRef.current = null
          renderAttemptedRef.current = false
        }
      }
    }, [])

    return (
      <div className="flex justify-center my-4">
        <div ref={containerRef} />
      </div>
    )
  }
)

Turnstile.displayName = 'Turnstile'
